import { useState } from "react";

const C = {
  navy: "#1A2B4A",
  navyMid: "#243557",
  red: "#C53D3F",
  redLight: "#fdf1f1",
  redDark: "#a32f31",
  border: "#e2e6ee",
  bg: "#F5F7FB",
  white: "#FFFFFF",
  muted: "#6B7280",
  mutedLt: "#9CA3AF",
  text: "#111827",
  success: "#059669",
  successBg: "#ecfdf5",
  error: "#DC2626",
};

const f = {
  display: "'Playfair Display', Georgia, serif",
  body: "'DM Sans', 'Segoe UI', sans-serif",
};

/* ── Tiny helpers ────────────────────────────────── */
const inputBase = {
  width: "100%",
  padding: "11px 14px",
  border: `1.5px solid ${C.border}`,
  borderRadius: "8px",
  fontSize: "14px",
  fontFamily: f.body,
  color: C.navy,
  background: C.white,
  outline: "none",
  transition: "border-color .18s, box-shadow .18s",
};

const focus = (e) => {
  e.target.style.borderColor = C.red;
  e.target.style.boxShadow = "0 0 0 3px rgba(197,61,63,0.1)";
};
const blur = (e) => {
  e.target.style.borderColor = C.border;
  e.target.style.boxShadow = "none";
};

const Label = ({ children }) => (
  <label style={{ fontSize: "13px", fontWeight: "700", color: C.navy, marginBottom: "6px", display: "block" }}>
    {children}
  </label>
);

const FieldInput = ({ ...props }) => (
  <input {...props} style={inputBase} onFocus={focus} onBlur={blur} />
);

const FieldSelect = ({ children, ...props }) => (
  <select
    {...props}
    style={{
      ...inputBase,
      paddingRight: "36px",
      appearance: "none",
      WebkitAppearance: "none",
      cursor: "pointer",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 14px center",
    }}
    onFocus={focus}
    onBlur={blur}
  >
    {children}
  </select>
);

const FieldTextarea = ({ ...props }) => (
  <textarea
    {...props}
    style={{ ...inputBase, resize: "vertical", minHeight: "110px" }}
    onFocus={focus}
    onBlur={blur}
  />
);

/* ── Info Card ───────────────────────────────────── */
const InfoCard = ({ icon, title, children, meta, link }) => (
  <div style={{
    display: "flex", gap: "18px", padding: "22px 24px",
    background: C.white, borderRadius: "10px",
    border: `1px solid ${C.border}`,
    borderLeft: `4px solid ${C.red}`,
    marginBottom: "14px",
  }}>
    <span style={{ fontSize: "22px", flexShrink: 0, marginTop: "2px" }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <p style={{ fontSize: "14px", fontWeight: "700", color: C.navy, marginBottom: "5px" }}>{title}</p>
      <p style={{ fontSize: "13.5px", color: C.muted, lineHeight: "1.65", marginBottom: meta || link ? "6px" : 0 }}>{children}</p>
      {meta && <p style={{ fontSize: "12.5px", color: C.red, fontWeight: "700" }}>{meta}</p>}
      {link && <a href={link.href} style={{ fontSize: "13px", color: C.red, fontWeight: "700", textDecoration: "none" }}>{link.label} →</a>}
    </div>
  </div>
);

/* ── Department Card ─────────────────────────────── */
const DeptCard = ({ icon, title, desc, phone, email }) => (
  <div style={{
    background: C.white, borderRadius: "12px",
    border: `1px solid ${C.border}`,
    padding: "32px 28px", textAlign: "center",
    transition: "box-shadow .2s",
  }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 28px rgba(26,43,74,0.1)"}
    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
  >
    <div style={{
      width: "60px", height: "60px", borderRadius: "12px",
      background: C.redLight, border: `1.5px solid ${C.red}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: "26px", margin: "0 auto 18px",
    }}>{icon}</div>
    <p style={{ fontFamily: f.display, fontSize: "17px", fontWeight: "700", color: C.navy, marginBottom: "8px" }}>{title}</p>
    <p style={{ fontSize: "13.5px", color: C.muted, marginBottom: "16px", lineHeight: "1.6" }}>{desc}</p>
    <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginBottom: "20px" }}>
      <span style={{ fontSize: "13px", color: C.muted, fontWeight: "600" }}>📞 {phone}</span>
      <span style={{ fontSize: "13px", color: C.muted, fontWeight: "600" }}>✉️ {email}</span>
    </div>
    <a href={`mailto:${email}`} style={{
      display: "inline-block", padding: "9px 22px",
      background: C.red, color: C.white, borderRadius: "7px",
      fontSize: "12.5px", fontWeight: "700", textDecoration: "none",
      letterSpacing: "0.4px", textTransform: "uppercase",
    }}>Contact Department</a>
  </div>
);

/* ── Visit Card ──────────────────────────────────── */
const VisitCard = ({ icon, title, desc, link }) => (
  <div style={{
    background: C.white, borderRadius: "10px",
    border: `1px solid ${C.border}`, padding: "26px 22px",
    textAlign: "center",
  }}>
    <div style={{ fontSize: "28px", marginBottom: "12px" }}>{icon}</div>
    <p style={{ fontSize: "15px", fontWeight: "700", color: C.navy, marginBottom: "8px" }}>{title}</p>
    <p style={{ fontSize: "13.5px", color: C.muted, marginBottom: "14px", lineHeight: "1.6" }}>{desc}</p>
    <a href={link} style={{ fontSize: "13px", color: C.red, fontWeight: "700", textDecoration: "none" }}>Learn More →</a>
  </div>
);

/* ── Main Component ──────────────────────────────── */
export default function Contact() {
  const blank = { name: "", email: "", phone: "", subject: "", message: "", studentLevel: "", inquiryType: "general" };
  const [form, setForm] = useState(blank);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    if (status) setStatus(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setStatus("success");
      setForm(blank);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 2000);
  };

  return (
    <div style={{ fontFamily: f.body, background: C.bg, color: C.text, lineHeight: "1.6" }}>

      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
        padding: "72px 24px 64px", textAlign: "center",
      }}>
        <div style={{ maxWidth: "580px", margin: "0 auto" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            padding: "6px 20px", background: "rgba(197,61,63,0.15)",
            border: "1px solid rgba(197,61,63,0.4)", borderRadius: "100px",
            color: "#f4a0a1", fontSize: "11px", fontWeight: "700",
            letterSpacing: "1.8px", textTransform: "uppercase", marginBottom: "18px",
          }}>
            <span style={{ width: "6px", height: "6px", background: C.red, borderRadius: "50%", display: "inline-block" }} />
            Contact Us
          </div>
          <h1 style={{ fontFamily: f.display, fontSize: "clamp(1.9rem,4vw,2.8rem)", fontWeight: "700", color: C.white, marginBottom: "14px", lineHeight: "1.2" }}>
            Get In Touch
          </h1>
          <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.72)", maxWidth: "480px", margin: "0 auto" }}>
            We're here to answer your questions. Reach out to Star Hacs International School anytime.
          </p>
        </div>
      </section>

      {/* Main: Info + Form */}
      <section style={{ padding: "60px 24px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.25fr)", gap: "48px", alignItems: "start" }}>

          {/* Left: Info */}
          <div>
            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "1.8px", textTransform: "uppercase", color: C.red, display: "block", marginBottom: "6px" }}>Contact Information</span>
            <h2 style={{ fontFamily: f.display, fontSize: "clamp(1.5rem,2.5vw,2rem)", fontWeight: "700", color: C.navy, marginBottom: "6px", lineHeight: "1.25" }}>
              Multiple Ways to Reach Us
            </h2>
            <p style={{ fontSize: "14px", color: C.muted, marginBottom: "28px" }}>Choose the method that works best for you.</p>

            <InfoCard icon="📍" title="Our Location" link={{ href: "#map", label: "View on Map" }}>
              Accra, Greater Accra Region, Ghana
            </InfoCard>
            <InfoCard icon="📞" title="Phone Numbers" meta="Mon – Fri: 7:30 AM – 4:30 PM">
              Main Office: +233 59 896 8300<br />Admissions: +233 59 703 4048
            </InfoCard>
            <InfoCard icon="✉️" title="Email Addresses" meta="Response within 24 hours">
              starhacsinternational@gmail.com
            </InfoCard>
            <InfoCard icon="🕒" title="Office Hours">
              <strong style={{ color: C.navy }}>Mon – Fri:</strong> 7:30 AM – 4:30 PM<br />
              <strong style={{ color: C.navy }}>Saturday:</strong> 9:00 AM – 1:00 PM<br />
              <strong style={{ color: C.navy }}>Sunday & Holidays:</strong> Closed
            </InfoCard>

            {/* Emergency box */}
            <div style={{
              display: "flex", gap: "18px", padding: "24px 26px",
              background: `linear-gradient(135deg, ${C.red}, ${C.redDark})`,
              borderRadius: "10px", color: C.white, marginTop: "4px",
            }}>
              <span style={{ fontSize: "24px", flexShrink: 0, marginTop: "2px" }}>🚨</span>
              <div>
                <p style={{ fontWeight: "700", fontSize: "15px", marginBottom: "4px" }}>Emergency Contact</p>
                <p style={{ fontSize: "13px", opacity: 0.85, marginBottom: "8px" }}>For urgent matters outside office hours</p>
                <p style={{ fontSize: "20px", fontWeight: "700", letterSpacing: "-0.5px", marginBottom: "2px" }}>+233 59 896 8300</p>
                <p style={{ fontSize: "12px", opacity: 0.75, fontStyle: "italic" }}>Available 24/7</p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div style={{
            background: C.white, borderRadius: "14px",
            border: `1px solid ${C.border}`,
            padding: "44px 44px",
            boxShadow: "0 2px 20px rgba(26,43,74,0.05)",
          }}>
            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "1.8px", textTransform: "uppercase", color: C.red, display: "block", marginBottom: "6px" }}>Message Us</span>
            <h2 style={{ fontFamily: f.display, fontSize: "1.75rem", fontWeight: "700", color: C.navy, marginBottom: "6px" }}>Send a Message</h2>
            <p style={{ fontSize: "14px", color: C.muted, marginBottom: "28px" }}>Fill out the form and we'll get back to you promptly.</p>

            {/* Status */}
            {status === "success" && (
              <div style={{
                display: "flex", alignItems: "center", gap: "14px",
                padding: "14px 18px", background: C.successBg,
                border: `1px solid rgba(5,150,105,0.3)`,
                borderLeft: `4px solid ${C.success}`,
                borderRadius: "8px", marginBottom: "24px",
              }}>
                <span style={{ width: "22px", height: "22px", borderRadius: "50%", background: C.success, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "700", flexShrink: 0 }}>✓</span>
                <p style={{ fontSize: "13.5px", color: "#065f46", fontWeight: "600", margin: 0 }}>
                  Thank you for contacting us! We'll respond within 24 hours.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div><Label>Full Name *</Label><FieldInput type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" required /></div>
                <div><Label>Email Address *</Label><FieldInput type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" required /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div><Label>Phone Number</Label><FieldInput type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+233 XX XXX XXXX" /></div>
                <div>
                  <Label>Inquiry Type *</Label>
                  <FieldSelect name="inquiryType" value={form.inquiryType} onChange={handleChange} required>
                    <option value="general">General Inquiry</option>
                    <option value="admissions">Admissions</option>
                    <option value="academics">Academic Programs</option>
                    <option value="fees">Fee Information</option>
                    <option value="tour">Campus Tour Request</option>
                    <option value="employment">Employment Opportunities</option>
                    <option value="other">Other</option>
                  </FieldSelect>
                </div>
              </div>
              <div>
                <Label>Student Level (If Applicable)</Label>
                <FieldSelect name="studentLevel" value={form.studentLevel} onChange={handleChange}>
                  <option value="">Select level</option>
                  <optgroup label="Pre-School">
                    <option value="toddlers">Toddlers</option>
                    <option value="reception1">Reception 1</option>
                    <option value="reception2">Reception 2</option>
                  </optgroup>
                  <optgroup label="Primary School">
                    {["Grade 1","Grade 2","Grade 3","Grade 4","Grade 5","Grade 6"].map((g,i) => (
                      <option key={i} value={`grade${i+1}`}>{g}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Junior High">
                    <option value="jhs1">JHS 1</option>
                  </optgroup>
                </FieldSelect>
              </div>
              <div><Label>Subject *</Label><FieldInput type="text" name="subject" value={form.subject} onChange={handleChange} placeholder="Brief subject line" required /></div>
              <div><Label>Message *</Label><FieldTextarea name="message" value={form.message} onChange={handleChange} placeholder="Please provide details about your inquiry…" rows={5} required /></div>

              <button
                type="submit"
                disabled={sending}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "10px",
                  padding: "13px 32px", background: sending ? C.mutedLt : C.red,
                  color: C.white, border: "none", borderRadius: "8px",
                  fontSize: "14px", fontWeight: "700", fontFamily: f.body,
                  cursor: sending ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px", marginTop: "4px",
                  transition: "opacity .15s, transform .15s",
                }}
                onMouseEnter={e => { if (!sending) { e.target.style.opacity = "0.88"; e.target.style.transform = "translateY(-1px)"; }}}
                onMouseLeave={e => { e.target.style.opacity = "1"; e.target.style.transform = "none"; }}
              >
                {sending ? (
                  <><span style={{ width: "16px", height: "16px", border: "2px solid rgba(255,255,255,0.35)", borderTopColor: C.white, borderRadius: "50%", animation: "spin .7s linear infinite", display: "inline-block" }} />Sending…</>
                ) : (
                  <><span>📧</span> Send Message</>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Departments */}
      <section style={{ padding: "60px 24px 72px", background: C.white }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "44px" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "1.8px", textTransform: "uppercase", color: C.red, display: "block", marginBottom: "8px" }}>Departments</span>
            <h2 style={{ fontFamily: f.display, fontSize: "clamp(1.6rem,3vw,2.2rem)", fontWeight: "700", color: C.navy, marginBottom: "10px" }}>Department Contacts</h2>
            <p style={{ fontSize: "15px", color: C.muted, maxWidth: "520px", margin: "0 auto" }}>Connect directly with specific departments for specialised assistance.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "24px" }}>
            <DeptCard icon="🎓" title="Admissions Office" desc="Enrollment, applications, and campus tours" phone="+233 59 703 4048" email="starhacsinternational@gmail.com" />
            <DeptCard icon="💼" title="Administration" desc="General admin and school operations" phone="+233 59 896 8300" email="starhacsinternational@gmail.com" />
          </div>
        </div>
      </section>

      {/* Visit / Map */}
      <section style={{ padding: "60px 24px 72px", background: C.bg }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "44px" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "1.8px", textTransform: "uppercase", color: C.red, display: "block", marginBottom: "8px" }}>Location</span>
            <h2 style={{ fontFamily: f.display, fontSize: "clamp(1.6rem,3vw,2.2rem)", fontWeight: "700", color: C.navy, marginBottom: "10px" }}>Visit Our Campus</h2>
            <p style={{ fontSize: "15px", color: C.muted, maxWidth: "480px", margin: "0 auto" }}>Experience our facilities and meet our community in person.</p>
          </div>

          {/* Map placeholder */}
          <div style={{
            background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
            borderRadius: "14px", padding: "56px 40px",
            textAlign: "center", color: C.white, marginBottom: "32px",
          }}>
            <div style={{ fontSize: "40px", marginBottom: "16px" }}>🗺️</div>
            <h3 style={{ fontFamily: f.display, fontSize: "22px", fontWeight: "700", marginBottom: "8px" }}>Star Hacs International School</h3>
            <p style={{ fontSize: "15px", opacity: 0.75, marginBottom: "28px" }}>Accra, Greater Accra Region, Ghana</p>
            <div style={{ display: "flex", justifyContent: "center", gap: "32px", marginBottom: "32px", flexWrap: "wrap" }}>
              {[["🚗","Ample Parking"],["♿","Accessible"],["🛡️","Secure Campus"]].map(([em,lbl]) => (
                <div key={lbl} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "18px" }}>{em}</span>
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>{lbl}</span>
                </div>
              ))}
            </div>
            <a href="#directions" style={{
              display: "inline-block", padding: "12px 28px",
              background: C.white, color: C.navy, borderRadius: "8px",
              fontWeight: "700", fontSize: "14px", textDecoration: "none",
              letterSpacing: "0.3px",
            }}>Get Directions</a>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "20px" }}>
            <VisitCard icon="📅" title="Schedule a Tour" desc="Book a personalised campus tour and meet our team." link="/contact" />
            <VisitCard icon="🅿️" title="Parking Information" desc="Free visitor parking available at the main entrance." link="#parking" />
            <VisitCard icon="👥" title="Group Visits" desc="Special arrangements for educational group tours." link="#group" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "72px 24px", background: C.white }}>
        <div style={{ maxWidth: "680px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: f.display, fontSize: "clamp(1.7rem,3vw,2.3rem)", fontWeight: "700", color: C.navy, marginBottom: "12px" }}>Ready to Take the Next Step?</h2>
          <p style={{ fontSize: "15px", color: C.muted, marginBottom: "36px" }}>Explore our programmes or apply for admission today.</p>
          <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap" }}>
            {[
              { href: "/application", label: "Apply Now", primary: true },
              { href: "/admissions", label: "View Admissions Info", primary: false },
            ].map(({ href, label, primary }) => (
              <a key={label} href={href} style={{
                display: "inline-block", padding: "13px 30px",
                background: primary ? C.red : C.white,
                color: primary ? C.white : C.red,
                border: `1.5px solid ${C.red}`,
                borderRadius: "8px", fontWeight: "700", fontSize: "14px",
                textDecoration: "none", letterSpacing: "0.2px",
                transition: "opacity .15s, transform .15s",
              }}
                onMouseEnter={e => { e.target.style.opacity = "0.88"; e.target.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.target.style.opacity = "1"; e.target.style.transform = "none"; }}
              >{label}</a>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:wght@400;500;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @media (max-width: 860px) {
          .contact-two-col { grid-template-columns: 1fr !important; }
          .contact-form-two { grid-template-columns: 1fr !important; }
          .dept-grid { grid-template-columns: 1fr !important; }
          .visit-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .cta-row { flex-direction: column !important; align-items: stretch !important; }
          .cta-row a { text-align: center !important; }
        }
      `}</style>
    </div>
  );
}